import { environment } from '@app/environments/environment';
import {
    EsResponseManyModel,
    EsResponseModel,
    LinkspaceDocumentModel,
} from '@app/models/elastic-search';
import { EsServiceBase } from '@app/services/abstracts/es-service.base';
import { cache, cacheKey, cacheTtl } from '@app/services/cache/cache.helpers';

export class EsLinkspacesService extends EsServiceBase {
    readonly #linkspaceEsIndexName = 'noc-linkspace';
    readonly #linkspaceEsIndexVersion = environment.LINKSPACES_ES_VERSION;
    readonly #listingLinkspaceSourceFields = [
        'pageType',
        'sections.id',
        'sections.name',
        'sections.links.name',
        'sections.links.url',
    ];

    private static _instance: EsLinkspacesService;

    protected get indexName(): string {
        return `${this.#linkspaceEsIndexName}-${this.#linkspaceEsIndexVersion}`;
    }

    public static get instance(): EsLinkspacesService {
        if (!this._instance) {
            this._instance = new EsLinkspacesService();
        }
        return this._instance;
    }

    /**
     * Load a linkspace document by ID, e.g.
     *   GET /noc-linkspace-3/_doc/city-{geoId}
     *   GET /noc-linkspace-3/_doc/cityCategory-{geoId}-{paramValue}
     *   GET /noc-linkspace-3/_doc/accommodation-{accommodationId}
     */
    public async loadById(docId: string): Promise<LinkspaceDocumentModel | null> {
        try {
            const response: EsResponseModel<LinkspaceDocumentModel> = await cache(
                cacheKey('linkspace:by-id', { indexName: this.indexName, docId }),
                () => this.get<LinkspaceDocumentModel>(this.indexName, docId),
                cacheTtl('APP_CACHE_LINKSPACE_BY_ID', 3600),
            );
            return response?._source ?? null;
        } catch (error) {
            return null;
        }
    }

    public async loadByIdSlim(docId: string): Promise<LinkspaceDocumentModel | null> {
        try {
            const response: EsResponseModel<LinkspaceDocumentModel> =
                await this.get<LinkspaceDocumentModel>(this.indexName, docId, '_doc', {
                    _source: this.#listingLinkspaceSourceFields.join(','),
                });
            return response?._source ?? null;
        } catch (error) {
            return null;
        }
    }

    public async loadAccommodationLinkspaces(
        accommodationId: string,
        cityId: string,
        categoryId: string,
    ): Promise<EsResponseManyModel<LinkspaceDocumentModel> | null> {
        try {
            const response: EsResponseManyModel<LinkspaceDocumentModel> = await cache(
                cacheKey('linkspace:accommodation', {
                    indexName: this.indexName,
                    accommodationId,
                    cityId,
                    categoryId,
                }),
                () =>
                    this.getMany<LinkspaceDocumentModel>(this.indexName, {
                        size: 3,
                        track_total_hits: false,
                        _source: this.#listingLinkspaceSourceFields,
                        query: {
                            bool: {
                                filter: [
                                    {
                                        terms: {
                                            _id: [
                                                `accommodation-${accommodationId}`,
                                                `city-${cityId}`,
                                                `cityCategory-${cityId}-${categoryId}`,
                                            ],
                                        },
                                    },
                                ],
                            },
                        },
                    }),
                cacheTtl('APP_CACHE_LINKSPACE_ACCOMMODATION', 3600),
            );
            return response ?? null;
        } catch (error) {
            return null;
        }
    }
}
